import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { IDEVRA, STATUS_META } from "@/data/idevra";
import { sfx } from "@/lib/sound";
import LiveMark from "@/pages/idevra/LiveMark";

const SLIDE_MS = 6500;
const EXIT_EVENTS = ["pointerdown", "mousemove", "keydown", "touchstart", "wheel"];

export default function AttractMode({ onExit }) {
  const [i, setI] = useState(0);

  useEffect(() => {
    sfx.whoosh();
    const iv = setInterval(() => {
      setI((x) => (x + 1) % IDEVRA.ventures.length);
      sfx.tick();
    }, SLIDE_MS);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const exit = () => onExit();
    const t = setTimeout(
      () => EXIT_EVENTS.forEach((e) => window.addEventListener(e, exit, { passive: true })),
      900
    );
    return () => {
      clearTimeout(t);
      EXIT_EVENTS.forEach((e) => window.removeEventListener(e, exit));
    };
  }, [onExit]);

  const v = IDEVRA.ventures[i];

  return (
    <motion.div
      className="fixed inset-0 z-[55] overflow-hidden bg-[#0B1220]"
      data-testid="attract-mode"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.8 }}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={v.slug}
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.1 }}
        >
          <motion.img
            src={v.gallery}
            alt={v.name}
            className="h-full w-full object-cover"
            initial={{ scale: 1 }}
            animate={{ scale: 1.1 }}
            transition={{ duration: SLIDE_MS / 1000 + 1.5, ease: "linear" }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B1220] via-[#0B1220]/40 to-[#0B1220]/10" />
          <div className="absolute bottom-20 left-6 sm:bottom-24 sm:left-14 max-w-xl">
            <div className="f-mono text-[10px] tracking-[0.35em] text-[#38BDF8]">
              {String(i + 1).padStart(2, "0")} · {v.sector.toUpperCase()}
            </div>
            <motion.h2
              className="f-orbitron mt-3 text-3xl font-black text-white sm:text-5xl"
              initial={{ y: 24, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              {v.name}
            </motion.h2>
            <motion.p
              className="mt-4 text-sm leading-relaxed text-slate-300"
              initial={{ y: 18, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.5 }}
            >
              {v.summary}
            </motion.p>
            <div className={`iv-status mt-4 ${STATUS_META[v.status].cls} !text-[10px]`}>
              {STATUS_META[v.status].label}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      <div className="absolute left-6 top-6 flex items-center gap-3 sm:left-10 sm:top-8">
        <LiveMark className="w-7" />
        <span className="f-mono text-[9px] tracking-[0.35em] text-slate-400">
          IDEVRA · VENTURES SHOWCASE
        </span>
      </div>

      <div className="absolute bottom-8 right-6 flex gap-2 sm:right-14">
        {IDEVRA.ventures.map((x, j) => (
          <span
            key={x.slug}
            className={`h-1.5 rounded-full transition-all duration-500 ${
              j === i ? "w-6 bg-[#38BDF8]" : "w-1.5 bg-slate-600"
            }`}
          />
        ))}
      </div>

      <div className="iv-press-start absolute bottom-8 left-1/2 -translate-x-1/2 f-mono text-[9px] tracking-[0.35em] text-slate-400">
        TOUCH ANY CONTROL TO RESUME
      </div>
    </motion.div>
  );
}
